import { useEffect, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { relativeTime } from '../lib/format';
import Icon from './Icon';

async function markAllNotificationsRead(uid) {
  const snap = await getDocs(
    query(collection(db, 'notifications', uid, 'items'), where('read', '==', false)),
  );
  if (snap.empty) return;
  const docs = snap.docs;
  for (let i = 0; i < docs.length; i += 450) {
    const chunk = docs.slice(i, i + 450);
    const batch = writeBatch(db);
    chunk.forEach((d) => batch.update(d.ref, { read: true }));
    await batch.commit();
  }
}

export default function NotificationsMenu({ user, open, onClose, onOpenListing, onOpenConversation }) {
  const [items, setItems] = useState([]);
  const [clearing, setClearing] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const panelRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      setMounted(true);
      const t = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setVisible(true));
      });
      return () => window.cancelAnimationFrame(t);
    }
    setVisible(false);
    const t = window.setTimeout(() => setMounted(false), 180);
    return () => window.clearTimeout(t);
  }, [open]);

  // Leaving the menu marks everything as read (clears dots + bell badge).
  useEffect(() => {
    if (open || !wasOpenRef.current || !user) return undefined;
    wasOpenRef.current = false;
    markAllNotificationsRead(user.uid).catch(() => {});
    return undefined;
  }, [open, user]);

  useEffect(() => {
    if (!user || !mounted) return undefined;
    const q = query(
      collection(db, 'notifications', user.uid, 'items'),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(
      q,
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setItems([]),
    );
  }, [user, mounted]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const onPointer = (e) => {
      if (e.target.closest('.notifications-anchor')) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      window.addEventListener('pointerdown', onPointer);
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [open, onClose]);

  if (!mounted) return null;

  const removeOne = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    await deleteDoc(doc(db, 'notifications', user.uid, 'items', item.id));
  };

  const clearAll = async () => {
    if (!items.length || clearing) return;
    setClearing(true);
    try {
      for (let i = 0; i < items.length; i += 450) {
        const chunk = items.slice(i, i + 450);
        const batch = writeBatch(db);
        chunk.forEach((item) => {
          batch.delete(doc(db, 'notifications', user.uid, 'items', item.id));
        });
        await batch.commit();
      }
    } finally {
      setClearing(false);
    }
  };

  return (
    <div
      ref={panelRef}
      className={`notifications-menu ${visible ? 'is-open' : ''}`}
      role="dialog"
      aria-label="Notifications"
    >
      <div className="notifications-menu-header">
        <b>Notifications</b>
        {items.length > 0 ? (
          <button
            type="button"
            className="notifications-menu-clear"
            onClick={clearAll}
            disabled={clearing}
          >
            Clear all
          </button>
        ) : null}
      </div>

      {items.length ? (
        <div className="notifications-menu-list">
          {items.map((item) => {
            const listingId = item.data?.listingId || item.listingId;
            const conversationId = item.data?.conversationId;
            const openItem = () => {
              onClose?.();
              if (listingId) onOpenListing?.(listingId);
              else if (conversationId) onOpenConversation?.(conversationId);
            };
            return (
              <div key={item.id} className="notifications-menu-row">
                <button type="button" className="notifications-menu-main" onClick={openItem}>
                  <div className={`notif-dot ${item.read ? '' : 'unread'}`} />
                  <div className="notifications-menu-copy">
                    <b>{item.title || 'Notification'}</b>
                    {item.body ? <p>{item.body}</p> : null}
                    <small>{relativeTime(item.createdAt)}</small>
                  </div>
                </button>
                <button
                  type="button"
                  className="notifications-menu-delete"
                  aria-label="Delete notification"
                  title="Delete"
                  onClick={(e) => removeOne(e, item)}
                >
                  <Icon name="trash" size={16} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="notifications-menu-empty">
          <Icon name="bell" size={22} />
          <p>You’re all caught up</p>
        </div>
      )}
    </div>
  );
}
